import { FileDownload } from '@mui/icons-material';
import { Button, Menu, MenuItem } from '@mui/material';
import { DataGridProps, GridCsvExportOptions } from '@mui/x-data-grid';
import { QueryDefinition } from '@reduxjs/toolkit/dist/query';
import { LazyQueryTrigger } from '@reduxjs/toolkit/dist/query/react/buildHooks';
import { useAuth } from 'hooks';
import { useCallback, useRef, useState } from 'react';
import { useAppDispatch } from 'store';

import { NapiResultType } from 'types';

import { DataTableColumn } from '../../DataTable';
import { BackOfficeSession } from '../../opsMapping';
import { mapDataToCsv } from './helpers';
import { activateSnackbar } from 'store/features/ui';
import { ElasticsearchDeviceBodyConfig } from 'types/ElasticsearchQueries';

export interface CustomCsvOptions extends GridCsvExportOptions {
    query?: LazyQueryTrigger<
        QueryDefinition<any, any, any, NapiResultType<any>, any>
    >;
    queryConfig?: ElasticsearchDeviceBodyConfig;
    dataFormatter?: (data: any) => any[];
    rowFormatter?: (
        rawRow: any,
        shownFields: string[],
        /** This function formats values based on the current column api */
        valueFormatter: (
            field: string,
            value: any,
            row: Record<string, any>
        ) => any
    ) => any[];
    supplierId?: string;
}

export interface GridToolbarExportButtonProps
    extends Omit<DataGridProps, 'rows'> {
    columnVisibilityModel: BackOfficeSession['visibilityModel'];
    columns: DataTableColumn[];
    gridId: string;
    csvOptions?: CustomCsvOptions;
}

export function GridToolbarExportButton({
    columns,
    columnVisibilityModel,
    gridId,
    csvOptions,
}: GridToolbarExportButtonProps) {
    const [open, setOpen] = useState(false);
    const { user } = useAuth();
    const dispatch = useAppDispatch();
    const query = csvOptions?.query;

    const buttonRef = useRef<HTMLButtonElement>(null);

    const handleExport = useCallback(async () => {
        const initialQueryConfig = {
            ...csvOptions?.queryConfig,
            ...{
                limit: 100,
                offset: 0,
            },
        };

        if (user?.protected && query) {
            const csvQuery = async () => {
                const dataFormatter =
                    csvOptions?.dataFormatter ?? defaultDataFormatter;

                const supplierId =
                    csvOptions.supplierId ?? user?.protected?.supplierId ?? '';
                let total = 0;

                if (supplierId.toString() === '0') {
                    const error = 'Do not export supplier 0 device list.';
                    dispatch(
                        activateSnackbar({
                            message: error,
                            severity: 'error',
                        })
                    );
                    throw new Error(error);
                }

                const { data } = await query({
                    userUid: user?.uid ?? '',
                    supplierId,
                    queryConfig: initialQueryConfig,
                });

                const rowData: Record<string, any>[] = [];
                if (data?.total) {
                    while (total * 100 < data.total) {
                        await query({
                            userUid: user?.uid ?? '',
                            supplierId,
                            queryConfig: {
                                ...initialQueryConfig,
                                offset: total * 100,
                            },
                        }).then((d) => {
                            if (d.data) {
                                rowData.push(dataFormatter(d.data));
                            }
                        });
                        total++;
                    }
                }
                return rowData.flat(1);
            };

            return await mapDataToCsv(
                csvQuery,
                columnVisibilityModel,
                columns,
                gridId,
                csvOptions
            );
        }
    }, [
        user?.protected,
        user?.uid,
        query,
        columnVisibilityModel,
        columns,
        gridId,
        csvOptions,
        dispatch,
    ]);

    return (
        <>
            <Button
                size="small"
                startIcon={<FileDownload />}
                id="basic-button"
                aria-controls={open ? 'basic-menu' : undefined}
                aria-haspopup="true"
                aria-expanded={open ? 'true' : undefined}
                onMouseUp={() => setOpen(!open)}
                ref={buttonRef}
            >
                Export
            </Button>
            <Menu
                onClose={() => setOpen(!open)}
                open={open}
                anchorEl={buttonRef.current}
            >
                <MenuItem onClick={handleExport}>
                    <Button>Export as Csv</Button>
                </MenuItem>
            </Menu>
        </>
    );
}

const defaultDataFormatter = (data: NapiResultType<unknown[]>) => data.results;
